import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FileText, Check, Copy, Bot } from 'lucide-react'
import { getPageMarkdown } from '../data/pageMarkdown'

interface CopyMarkdownButtonProps {
  section: string
  pageTitle?: string
  className?: string
}

function generateAIPrompt(md: string, pageTitle: string): string {
  return `# Solarch Documentation — ${pageTitle}

> This markdown was generated from the official Solarch documentation site (https://solarch.in).
> It includes the documentation content PLUS a knowledge graph analysis of the actual codebase.
> Paste this entire document into your AI coding agent for full context.

---

## Instructions for AI Agent

You are assisting with the **Solarch** project — a TypeScript Backend-as-a-Service.
Below you will find:
1. **Official documentation** for the relevant feature/module
2. **Knowledge graph data** extracted from the actual source code (+45k words, 1032 nodes, 2407 edges)

Use this context to:
- Answer questions about how features work
- Suggest code changes or additions
- Explain relationships between modules
- Debug issues by tracing through the graph

---

${md}

---

## End of Document
> Generated by Solarch Docs — https://solarch.in
`
}

export default function CopyMarkdownButton({ section, pageTitle, className = '' }: CopyMarkdownButtonProps) {
  const [copied, setCopied] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  const handleCopy = useCallback(async () => {
    const pageMd = getPageMarkdown(section)
    if (!pageMd) return

    const fullMarkdown = generateAIPrompt(pageMd.content, pageTitle || pageMd.title)

    try {
      await navigator.clipboard.writeText(fullMarkdown)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      // ignore
    }
  }, [section, pageTitle])

  const hasContent = !!getPageMarkdown(section)
  if (!hasContent) return null

  return (
    <div className={`relative inline-flex ${className}`}>
      <button
        onClick={handleCopy}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="group inline-flex items-center gap-2 rounded-lg border border-theme-hover bg-theme-muted px-3 py-2 text-xs font-medium text-theme-tertiary transition-all hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
        aria-label="Copy as Markdown for AI"
      >
        <Bot className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Copy as Markdown</span>
        <span className="sm:hidden">AI Copy</span>
        <AnimatePresence mode="wait" initial={false}>
          {copied ? (
            <motion.div
              key="check"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Check className="h-3 w-3 text-primary" />
            </motion.div>
          ) : (
            <motion.div
              key="copy"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Copy className="h-3 w-3 opacity-50 group-hover:opacity-100" />
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {showTooltip && !copied && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-theme-hover bg-theme-surface p-3 shadow-xl"
          >
            <div className="flex items-start gap-2">
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-xs font-medium text-theme">Copy for AI Agent</p>
                <p className="mt-1 text-[11px] leading-relaxed text-theme-tertiary">
                  Copies the full documentation + codebase knowledge graph as markdown. Paste into Claude, ChatGPT, or Cursor for complete context.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
